

import React, { useState, useEffect } from "react";
import { FaMapMarkerAlt, FaGlobeAmericas, FaInfoCircle, FaCloudSun, FaCloud, FaCloudRain, FaSnowflake, FaBolt, FaSmog, FaTemperatureHigh, FaWind, FaExclamationTriangle, FaTimesCircle, FaQuestionCircle, FaCheckCircle, FaDirections, FaCampground, FaSun, FaExclamationCircle } from "react-icons/fa";
import { useHeaderContext } from "../context/HeaderContext";
import YogyImg from "../assets/Yogy.jpg";
import GroqDrawer from "../components/GroqDrawer";
import { summarizeForHeader } from "../services/summarization";

// Helper to generate a Google Maps URL from a latLong string
function getGoogleMapsUrl(latLong) {
  if (!latLong || typeof latLong !== 'string') return undefined;
  // Try to extract lat/long from string like "lat: 37.8651, long: -119.5383"
  const latMatch = latLong.match(/lat:\s*([\d.-]+)/i);
  const longMatch = latLong.match(/long:\s*([\d.-]+)/i);
  if (latMatch && longMatch) {
    const lat = latMatch[1];
    const lng = longMatch[1];
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }
  return undefined;
}

// Use the same endpoint and structure as DebugPage.js WorkingTool
const blmTool = {
  id: 'blm',
  name: 'BLM Recreation Points',
  icon: '≡ƒÅò∩╕Å',
  description: 'Find BLM campgrounds',
  workingId: 'blm1',
  endpoint: "https://gis.blm.gov/arcgis/rest/services/recreation/BLM_Natl_Recreation_Sites_Facilities/MapServer/0/query?where=FacilityTypeDescription='Campground'&outFields=FacilityName,FacilityDescription,FacilityReservationURL,State&f=geojson"
};

const PlanPage = () => {
  // All useState hooks declared at the top
  const { groqContext, setGroqContext, location, setLocation } = useHeaderContext();

  const [groqLoading, setGroqLoading] = useState(false);
  const [groqError, setGroqError] = useState("");
  const [parkCodes, setParkCodes] = useState([]);
  const [selectedParkCode, setSelectedParkCode] = useState("");
  const [parkData, setParkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [alertsData, setAlertsData] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState("");
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [weatherData, setWeatherData] = useState(null);
  const [campgrounds, setCampgrounds] = useState([]);
  const [campgroundsLoading, setCampgroundsLoading] = useState(false);
  const [campgroundsError, setCampgroundsError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [blmSelectedIdx, setBlmSelectedIdx] = useState("");
  const [blmItems, setBlmItems] = useState([]);
  const [blmLoading, setBlmLoading] = useState(false);
  const [blmError, setBlmError] = useState("");
  const [selectedBlmCampground, setSelectedBlmCampground] = useState(null);

  const SUPABASE_API_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "YOUR_SUPABASE_ANON_KEY_HERE";
  const NPS_API_KEY = process.env.REACT_APP_NPS_API_KEY || "REACT_APP_NPS_API_KEY";

  // NPS dropdown caching logic
  useEffect(() => {
    // 1. Try to load from localStorage first
    const cached = localStorage.getItem("npsParks");
    let initialParks = [];
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          initialParks = parsed;
          setParkCodes(parsed);
          setSelectedParkCode(parsed[0]?.code || "");
        }
      } catch (e) {
        // ignore parse error, fallback to fetch
      }
    }

    // 2. Always fetch from Supabase in background
    const fetchParks = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `https://bylolucjhzsbbouoalah.supabase.co/functions/v1/get_nps?parkCode=all`,
          {
            headers: {
              apikey: SUPABASE_API_KEY,
              Authorization: `Bearer ${SUPABASE_API_KEY}`,
            },
          }
        );
        if (res.status === 403) {
          setError("403 Forbidden: Check your API key or permissions.");
          setLoading(false);
          return;
        }
        const text = await res.text();
        const data = JSON.parse(text);

        if (data?.park && Array.isArray(data.park)) {
          const parks = data.park.map((p) => ({ name: p.name, code: p.code }));
          setParkCodes(parks);
          // Only update selectedParkCode if not already set (e.g. on first load)
          setSelectedParkCode((prev) => prev || parks[0]?.code || "");
          localStorage.setItem("npsParks", JSON.stringify(parks));
        } else {
          console.error("Unexpected data format:", data);
          setError("Unexpected data format");
        }
      } catch (err) {
        console.error("Error fetching park codes:", err);
        setError("Failed to fetch park codes");
      } finally {
        setLoading(false);
      }
    };

    fetchParks();

    // 3. Listen for localStorage changes from other tabs
    const handleStorage = (e) => {
      if (e.key === "npsParks" && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setParkCodes(parsed);
            setSelectedParkCode((prev) => prev || parsed[0]?.code || "");
          }
        } catch {}
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const handleFetchPark = async () => {
    if (!selectedParkCode) return;
    setLoading(true);
    setParkData(null);
    setError("");
    setAlertsData(null);
    setAlertsError("");
    setAlertsLoading(true);
    setWeatherData(null);
    setWeatherError("");
    setWeatherLoading(false);
    setCampgrounds([]);
    setCampgroundsError("");
    setCampgroundsLoading(true);
    try {
      // Fetch park data
      const parkRes = await fetch(
        `https://bylolucjhzsbbouoalah.supabase.co/functions/v1/get_nps?parkCode=${selectedParkCode}`,
        {
          headers: {
            apikey: SUPABASE_API_KEY,
            Authorization: `Bearer ${SUPABASE_API_KEY}`,
          },
        }
      );
      if (parkRes.status === 403) {
        setError("403 Forbidden: Check your API key or permissions.");
        setLoading(false);
        setAlertsLoading(false);
        setCampgroundsLoading(false);
        return;
      }
      const parkText = await parkRes.text();
      const parkDataParsed = JSON.parse(parkText);
      setParkData(parkDataParsed);

      // Fetch alerts
      try {
        const alertsRes = await fetch(
          `https://developer.nps.gov/api/v1/alerts?parkCode=${selectedParkCode}&api_key=${NPS_API_KEY}`
        );
        if (alertsRes.ok) {
          const alertsJson = await alertsRes.json();
          setAlertsData(alertsJson);
        } else {
          setAlertsError(`Failed to fetch alerts: ${alertsRes.status}`);
        }
      } catch (err) {
        setAlertsError("Failed to fetch alerts");
      }
      setAlertsLoading(false);

      // Fetch weather for park
      const parkInfo = parkDataParsed?.park?.data?.[0];
      if (parkInfo && parkInfo.latLong) {
        const coords = extractLatLong(parkInfo.latLong);
        if (coords) {
          try {
            // Use env variable for weather API base, fallback to default if not set
            const WEATHER_API_BASE = process.env.REACT_APP_WEATHER_API || "https://api.weather.gov";
            const pointsRes = await fetch(`${WEATHER_API_BASE}/points/${coords.lat},${coords.long}`);
            if (pointsRes.ok) {
              const pointsJson = await pointsRes.json();
              const forecastUrl = pointsJson?.properties?.forecast;
              if (forecastUrl) {
                const forecastRes = await fetch(forecastUrl);
                if (forecastRes.ok) {
                  const weatherJson = await forecastRes.json();
                  setWeatherData(weatherJson);
                } else {
                  setWeatherError(`Failed to get weather forecast: ${forecastRes.status}`);
                }
              } else {
                setWeatherError("No forecast URL found for this location.");
              }
            } else {
              setWeatherError(`Failed to get weather grid: ${pointsRes.status}`);
            }
          } catch (err) {
            setWeatherError("Failed to fetch weather data");
          }
        } else {
          setWeatherError("Could not parse park coordinates for weather.");
        }
      } else {
        setWeatherError("No coordinates found for this park.");
      }
      setWeatherLoading(false);

      // Fetch campgrounds
      try {
        const campRes = await fetch(`https://developer.nps.gov/api/v1/campgrounds?parkCode=${selectedParkCode}&api_key=${NPS_API_KEY}`);
        if (campRes.ok) {
          const campJson = await campRes.json();
          setCampgrounds(campJson.data || []);
        } else {
          setCampgroundsError(`Failed to fetch campgrounds: ${campRes.status}`);
        }
      } catch (err) {
        setCampgroundsError("Failed to fetch campgrounds");
      }
      setCampgroundsLoading(false);

    } catch (err) {
      console.error("Error fetching park or alerts data:", err);
      setError("Failed to fetch park data");
      setAlertsError("Failed to fetch alerts");
      setAlertsLoading(false);
      setWeatherError("Failed to fetch weather");
      setCampgroundsError("Failed to fetch campgrounds");
      setCampgroundsLoading(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Unified summarization effect:
   * Whenever any relevant state (parkData, alertsData, weatherData, campgrounds, selectedBlmCampground)
   * changes, update the header summary with the latest summary.
   * This ensures both NPS park fetch and BLM campground selection update the header immediately.
   */
  useEffect(() => {
    if (!parkData?.park?.data?.[0]) return; // Only run when park data is fully loaded
    setGroqLoading(true);
    setGroqError("");
    const park = parkData.park.data[0];
    // Set location context to park coordinates and name
    if (park.latLong) {
      const latMatch = park.latLong.match(/lat:\s*([\d.-]+)/i);
      const longMatch = park.latLong.match(/long:\s*([\d.-]+)/i);
      if (latMatch && longMatch) {
        setLocation({
          name: park.fullName || park.name || "",
          lat: parseFloat(latMatch[1]),
          lon: parseFloat(longMatch[1]),
        });
      }
    }
    const inputObj = {
      fullName: park.fullName || park.name || "Unknown Park",
      description: park.description,
      states: park.states,
      designation: park.designation,
      latLong: park.latLong,
      url: park.url,
      weatherInfo: park.weatherInfo,
    };
    (async () => {
      try {
        const summary = await summarizeForHeader(inputObj);
        if (summary) setGroqContext(summary);
      } catch (err) {
        setGroqError(err.message || "Failed to summarize with Groq.");
      } finally {
        setGroqLoading(false);
      }
    })();
  }, [parkData, setGroqContext]);

  useEffect(() => {
    setBlmLoading(true);
    setBlmError("");
    fetch(blmTool.endpoint)
      .then((res) => {
        if (!res.ok) throw new Error(`BLM fetch failed: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const features = Array.isArray(json?.features) ? json.features : [];
        const mapped = features.map((f, i) => {
          const p = f?.properties || {};
          return {
            id: i,
            name: p.FacilityName || "Unnamed Campground",
            description: (p.FacilityDescription || "").toString(),
            state: p.State || "",
            url: p.FacilityReservationURL || "",
          };
        });
        setBlmItems(mapped);
      })
      .catch((err) => setBlmError(err.message || "Failed to fetch BLM campgrounds."))
      .finally(() => setBlmLoading(false));
  }, []);

  useEffect(() => {
    if (blmSelectedIdx === "") {
      setSelectedBlmCampground(null);
      return;
    }
    const item = blmItems.find((x) => String(x.id) === String(blmSelectedIdx));
    if (item) {
      setSelectedBlmCampground(item);
    }
  }, [blmSelectedIdx, blmItems]);

  // Helper to extract lat/long from NPS format "lat: 37.8651, long: -119.5383"
  function extractLatLong(latLongStr) {
    if (!latLongStr) return null;
    const latMatch = latLongStr.match(/lat:\s*([\d.-]+)/i);
    const longMatch = latLongStr.match(/long:\s*([\d.-]+)/i);
    if (latMatch && longMatch) {
      return { lat: latMatch[1], long: longMatch[1] };
    }
    return null;
  }

  // Fetch weather.gov data for given lat/long
  async function fetchWeather(lat, long) {
    setWeatherLoading(true);
    setWeatherError("");
    setWeatherData(null);
    try {
      // Use env variable for weather API base, fallback to default if not set
      const WEATHER_API_BASE = process.env.REACT_APP_WEATHER_API || "https://api.weather.gov";
      // Step 1: Get gridpoint
      const pointsRes = await fetch(`${WEATHER_API_BASE}/points/${lat},${long}`);
      if (!pointsRes.ok) {
        setWeatherError(`Failed to get weather grid: ${pointsRes.status}`);
        setWeatherLoading(false);
        return;
      }
      const pointsJson = await pointsRes.json();
      const forecastUrl = pointsJson?.properties?.forecast;
      if (!forecastUrl) {
        setWeatherError("No forecast URL found for this location.");
        setWeatherLoading(false);
        return;
      }
      // Step 2: Get forecast
      const forecastRes = await fetch(forecastUrl);
      if (!forecastRes.ok) {
        setWeatherError(`Failed to get weather forecast: ${forecastRes.status}`);
        setWeatherLoading(false);
        return;
      }
      const forecastJson = await forecastRes.json();
      setWeatherData(forecastJson);
    } catch (err) {
      setWeatherError("Failed to fetch weather data");
    } finally {
      setWeatherLoading(false);
    }
  }

  const toggleCollapse = (path) => {
    setCollapsed((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  // Safe checks for parkInfo and selectedBlmCampground
  const parkInfo = parkData?.park?.data?.[0] || {};
  const fullName = parkInfo.fullName || "N/A";
  const description = parkInfo.description || "N/A";
  const latitude = parkInfo.latitude || "N/A";
  const latLong = parkInfo.latLong || "N/A";
  const states = parkInfo.states || "N/A";
  const contacts = parkInfo.contacts || "N/A";
  const entranceFees = parkInfo.entranceFees || "N/A";
  const directionsInfo = parkInfo.directionsInfo || "N/A";
  const directionsUrl = parkInfo.directionsUrl || "N/A";
  const operatingHours = parkInfo.operatingHours || "N/A";
  const weatherInfo = parkInfo.weatherInfo || "N/A";
  const designation = parkInfo.designation || "N/A";
  const parkImage = parkInfo.images?.[0] || null;



  // Utility: strip simple HTML tags from BLM descriptions for the preview
  function stripHtml(str) {
    try {
      const div = document.createElement("div");
      div.innerHTML = str;
      return (div.textContent || div.innerText || "").trim();
    } catch {
      return str;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-lg border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
          <div className="flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-600 rounded-xl flex items-center justify-center mb-2 sm:mb-0">
              <FaGlobeAmericas className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white font-rye tracking-[0.1em]">
                Plan Your Park Visit
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1 text-base sm:text-lg">
                Explore National Parks Service and Bureau of Land Management data and alerts
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* GroqDrawer Trigger Button with Image */}
      <div className="fixed bottom-20 right-6 z-50">
        <button
          onClick={() => setDrawerOpen(true)}
          className="rounded-2xl shadow-lg border-2 border-white dark:border-gray-700 overflow-hidden p-0 bg-white dark:bg-gray-800 hover:scale-105 transition-transform duration-200"
          style={{ width: 88, height: 88 }}
        >
          <img
            src={YogyImg}
            alt="Open Groq Assistant"
            className="object-cover w-full h-full rounded-2xl"
          />
        </button>
      </div>
      <GroqDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <div className="text-gray-900 dark:text-gray-100">
          <p>This is a placeholder for your Groq Assistant. Add chat or LLM UI here.</p>
        </div>
      </GroqDrawer>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 space-y-6 sm:space-y-8">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <FaExclamationTriangle className="text-red-500 text-xl" />
              <span className="text-red-700 dark:text-red-300 font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Park Selection */}
        {groqLoading && (
          <div className="flex items-center gap-2 mt-2">
            <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            <span className="text-blue-700 dark:text-blue-300 text-sm">Summarizing for header...</span>
          </div>
        )}
        {groqError && (
          <div className="text-red-600 mt-2 text-sm">{groqError}</div>
        )}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-green-500 p-6">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <span> </span>
              Park Selection
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Choose a National Park to explore:
                </label>
                <select
                  value={selectedParkCode}
                  onChange={(e) => {
                    setSelectedParkCode(e.target.value);
                    setSelectedBlmCampground(null); // Reset BLM selection to force header update
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  disabled={loading && parkCodes.length === 0}
                >
                  <option value="" disabled>
                    Select a park
                  </option>
                  {parkCodes.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {loading && parkCodes.length === 0 && (
                  <div className="flex items-center gap-3 mt-2">
                    <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    <span className="text-gray-600 dark:text-gray-400">Loading parks...</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleFetchPark}
                disabled={loading || !selectedParkCode}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <FaMapMarkerAlt />
                    Fetch Park Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {false && (
        <div className="mt-8">
          <div className="p-4 border rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">{`${blmTool.icon} ${blmTool.name}`}</h3>
            </div>
            {blmLoading && <div className="text-sm text-black">Loading BLM campgroundsΓÇª</div>}
            {blmError && <div className="text-sm text-red-600">Error: {blmError}</div>}
            {!blmLoading && !blmError && (
              <>
                <label className="block text-sm font-medium mb-2">Choose a BLM campground:</label>
                <select
                  className="w-full px-3 py-2 border rounded bg-white text-black"
                  value={blmSelectedIdx}
                  onChange={(e) => setBlmSelectedIdx(e.target.value)}
                >
                  <option value="">-- Select a campground --</option>
                  {blmItems.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} {item.state ? `(${item.state})` : ""}
                    </option>
                  ))}
                </select>
                {/* Optional: show a tiny preview of the chosen item */}
                {selectedBlmCampground && (
                  <div className="mt-3 text-sm bg-white text-black p-3 rounded">
                    <div className="font-semibold">{selectedBlmCampground.name}</div>
                    {selectedBlmCampground.state && <div className="text-gray-700">State: {selectedBlmCampground.state}</div>}
                    {selectedBlmCampground.url && (
                      <a
                        href={selectedBlmCampground.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline break-all"
                      >
                        Reservation / Info
                      </a>
                    )}
                    {selectedBlmCampground.description && (
                      <p className="mt-2 text-gray-800 line-clamp-4">{stripHtml(selectedBlmCampground.description)}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        )}

        {/* National Park Section */}
        {parkData && (
          <div className="space-y-8">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-teal-500 p-6">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <span> </span>
                  Park Information
                </h2>
              </div>
              <div className="p-4 sm:p-6">
                {/* Park Header with Image */}
                {parkImage && (
                  <div className="mb-4 sm:mb-6">
                    <div className="relative rounded-xl overflow-hidden shadow-lg">
                      <img 
                        src={parkImage.url} 
                        alt={parkImage.altText || fullName}
                        className="w-full h-64 md:h-80 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 sm:p-4">
                        <h3 className="text-white text-lg sm:text-xl md:text-2xl font-bold mb-1">{fullName}</h3>
                        {parkImage.caption && (
                          <p className="text-white/90 text-sm">{parkImage.caption}</p>
                        )}
                        {parkImage.credit && (
                          <p className="text-white/70 text-xs mt-1">Photo: {parkImage.credit}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {/* Mobile-first layout with responsive breakpoints */}
                <div className="space-y-6">
                  {/* Alerts Section - Mobile first */}
                  <div className="lg:hidden">
                    <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <FaExclamationCircle className="text-xl" />
                        <h3 className="font-semibold text-gray-900 dark:text-white">Current Alerts</h3>
                      </div>
                      {alertsLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Loading alerts...</span>
                        </div>
                      ) : alertsError ? (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Unable to load alerts
                        </div>
                      ) : alertsData && alertsData.data && alertsData.data.length > 0 ? (
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {alertsData.data.map((alert, index) => (
                            <AlertCard key={alert.id || index} alert={alert} />
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                          <FaCheckCircle className="text-lg" />
                          <span className="text-sm font-medium">No active alerts</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Desktop layout */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Basic Park Info */}
                    <div className="lg:col-span-2 space-y-4">
                      {!parkImage && <InfoField label="Full Name" value={fullName} icon={<FaGlobeAmericas />} />}
                      <InfoField label="Designation" value={designation} icon={<FaInfoCircle />} />
                      <InfoField label="States" value={states} icon={<FaMapMarkerAlt />} />
                      <InfoField 
                        label="Coordinates" 
                        value={latLong} 
                        icon={<FaMapMarkerAlt />} 
                        link={getGoogleMapsUrl(latLong)}
                      />
                      <InfoField 
                        label="Description" 
                        value={description} 
                        icon={<FaInfoCircle />}
                      />
                      <InfoField label="Weather Info" value={weatherInfo} icon={<FaCloudSun />} />
                      <InfoField label="Directions Info" value={directionsInfo} icon={<FaDirections />} />
                      {directionsUrl !== "N/A" && (
                        <div className="flex items-start gap-2">
                          <FaDirections className="text-lg" />
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Directions URL:</span>
                            <a 
                              href={directionsUrl} 
                              className="text-blue-600 dark:text-blue-400 hover:underline ml-2 break-all"
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              {directionsUrl}
                            </a>
                          </div>
                        </div>
                      )}
                      {/* Weather Tool Box */}
                      <div className="mt-6">
                        <WeatherBox 
                          loading={weatherLoading} 
                          error={weatherError} 
                          data={weatherData} 
                        />
                      </div>
                      {/* Campgrounds Section */}
                      <div className="mt-6">
                        {campgroundsLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full"></div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Loading campgrounds...</span>
                          </div>
                        ) : campgroundsError ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{campgroundsError}</div>
                        ) : campgrounds && campgrounds.length > 0 ? (
                          <div>
                            <h4 className="font-semibold text-green-700 dark:text-green-300 mb-2 flex items-center gap-2"><FaCampground />Campgrounds ({campgrounds.length})</h4>
                            <ul className="space-y-2 max-h-48 overflow-y-auto">
                              {campgrounds.map((camp, idx) => (
                                <li key={camp.id || idx} className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded p-3">
                                  <div className="font-semibold text-gray-900 dark:text-white">{camp.name}</div>
                                  <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">{camp.description || "No description."}</div>
                                  {camp.url && (
                                    <a href={camp.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline text-xs">More Info</a>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {/* Alerts Section - Desktop only */}
                    <div className="hidden lg:block lg:col-span-1">
                      <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-4 h-full">
                        <div className="flex items-center gap-2 mb-4">
                          <FaExclamationCircle className="text-xl" />
                          <h3 className="font-semibold text-gray-900 dark:text-white">Current Alerts</h3>
                        </div>
                        {alertsLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Loading alerts...</span>
                          </div>
                        ) : alertsError ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Unable to load alerts
                          </div>
                        ) : alertsData && alertsData.data && alertsData.data.length > 0 ? (
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {alertsData.data.map((alert, index) => (
                              <AlertCard key={alert.id || index} alert={alert} />
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                            <FaCheckCircle className="text-lg" />
                            <span className="text-sm font-medium">No active alerts</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
// WeatherBox component for displaying weather.gov forecast
function WeatherBox({ loading, error, data }) {
  return (
    <div className="bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-2 mb-2">
  <FaCloudSun className="text-xl" />
        <h3 className="font-semibold text-gray-900 dark:text-white">Weather Forecast</h3>
      </div>
      {loading ? (
        <div className="flex items-center gap-2">
          <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          <span className="text-sm text-gray-600 dark:text-gray-400">Loading weather...</span>
        </div>
      ) : error ? (
        <div className="text-sm text-red-500 dark:text-red-400">{error}</div>
      ) : data && data.properties && data.properties.periods && data.properties.periods.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.properties.periods.slice(0, 6).map((period, idx) => (
            <div key={period.number || idx} className="bg-white dark:bg-gray-900/60 rounded-lg shadow p-3 flex flex-col gap-1 border border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-800 dark:text-gray-100">{period.name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{period.startTime ? new Date(period.startTime).toLocaleDateString() : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{getWeatherIcon(period.shortForecast)}</span>
                <span className="text-gray-700 dark:text-gray-200">{period.shortForecast}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <span><FaTemperatureHigh /> {period.temperature}°{period.temperatureUnit}</span>
                <span><FaWind /> {period.windSpeed} {period.windDirection}</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{period.detailedForecast}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-400">No weather data available.</div>
      )}
    </div>
  );
}

// Helper to get a weather icon based on forecast
function getWeatherIcon(forecast) {
  if (!forecast) return <FaCloud />;
  const f = forecast.toLowerCase();
  if (f.includes("sun") || f.includes("clear")) return <FaSun />;
  if (f.includes("cloud")) return <FaCloud />;
  if (f.includes("rain") || f.includes("shower")) return <FaCloudRain />;
  if (f.includes("snow")) return <FaSnowflake />;
  if (f.includes("thunder")) return <FaBolt />;
  if (f.includes("fog")) return <FaSmog />;
  return <FaTemperatureHigh />;
};

const InfoField = ({ label, value, icon, className = "", link }) => (
  <div className={`flex items-start gap-2 ${className}`}>
    <span className="text-lg flex-shrink-0">{icon}</span>
    <div className="min-w-0 flex-1">
      <span className="font-medium text-gray-700 dark:text-gray-300 block">{label}:</span>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 underline break-words hover:text-blue-800 dark:hover:text-blue-300"
        >
          {typeof value === 'object' ? JSON.stringify(value) : value}
        </a>
      ) : (
        <span className="text-gray-600 dark:text-gray-400 break-words">
          {typeof value === 'object' ? JSON.stringify(value) : value}
        </span>
      )}
    </div>
  </div>
);

const AlertCard = ({ alert }) => {
  const getAlertIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'closure':
        return <FaTimesCircle />;
      case 'caution':
        return <FaExclamationTriangle />;
      case 'information':
        return <FaInfoCircle />;
      case 'park closure':
        return <FaTimesCircle />;
      case 'danger':
        return <FaExclamationCircle />;
      default:
        return <FaQuestionCircle />;
    }
  };

  const getAlertColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'closure':
      case 'park closure':
      case 'danger':
        return 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20';
      case 'caution':
        return 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      case 'information':
        return 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20';
      default:
        return 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  return (
    <div className={`border rounded-lg p-3 ${getAlertColor(alert.category)}`}>
      <div className="flex items-start gap-2 mb-2">
        <span className="text-sm">{getAlertIcon(alert.category)}</span>
        <div className="min-w-0 flex-1">
          <h4 className="font-medium text-sm text-gray-900 dark:text-white line-clamp-2">
            {alert.title}
          </h4>
          {alert.category && (
            <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
              {alert.category}
            </span>
          )}
        </div>
      </div>
      {alert.description && (
        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3">
          {alert.description}
        </p>
      )}
      {alert.url && (
        <a 
          href={alert.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          Learn more ΓåÆ
        </a>
      )}
    </div>
  );
};

export default PlanPage;

